import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Translation } from 'react-i18next';
import styles from './ErrorBoundary.module.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
  fullPage?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private handleReload = () => {
    this.props.onReset?.();
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    return (
      <Translation ns="common">
        {(t) => {
          if (this.props.fullPage) {
            return (
              <div className={styles.fullPage}>
                <div className={styles.card}>
                  <h2 className={styles.title}>{t('somethingWentWrong')}</h2>
                  <p className={styles.message}>
                    {t('unexpectedErrorReload')}
                  </p>
                  <button className={styles.reloadBtn} onClick={() => window.location.reload()}>
                    {t('reload')}
                  </button>
                </div>
              </div>
            );
          }

          return (
            <div className={styles.inline}>
              <span>{t('somethingWentWrongInline')}</span>
              <button className={styles.inlineLink} onClick={this.handleReload}>
                {t('tryAgain')}
              </button>
            </div>
          );
        }}
      </Translation>
    );
  }
}
